import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { ListItemIcon } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { Building03Icon } from "@hugeicons-pro/core-stroke-rounded";
import ContactSearchAutoComplete from "components/common/ContactSearchAutoComplete";
import ContactType from "enums/ContactType";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { activateOnKey } from "utils/activateOnKey";

interface ContactBusinessCardProps {
  editing: boolean;
  businessName: string;
  businessLocation: string;
  parentContactId: string;
  onSelectBusiness: (businessId: string, businessName: string) => void;
  onOpenBusiness: (businessId: string) => void;
}

/** The business an individual belongs to: read-only, or a picker while editing. */
const ContactBusinessCard: React.FC<ContactBusinessCardProps> = ({
  editing,
  businessName,
  businessLocation,
  parentContactId,
  onSelectBusiness,
  onOpenBusiness,
}) => {
  const { t } = useTranslation();

  return (
    <Card variant="outlined" sx={{ padding: "var(--space-6)" }}>
      <Stack gap="var(--space-5)">
        <Typography variant="h5">{t("Business")}</Typography>

        {editing ? (
          <ContactSearchAutoComplete
            contactType={ContactType.Business}
            contactId={parentContactId}
            customPlaceholder={t("Associated business")}
            onSelectContact={(contact) => {
              const business = Array.isArray(contact) ? contact[0] : contact;
              onSelectBusiness(
                business?.id ?? "",
                business?.businessName ?? "",
              );
            }}
          />
        ) : parentContactId && businessName ? (
          <Inline
            gap="var(--space-3)"
            align="center"
            wrap={false}
            role="button"
            tabIndex={0}
            aria-label={t("ViewContactDetailsFor", { name: businessName })}
            onClick={() => onOpenBusiness(parentContactId)}
            onKeyDown={activateOnKey(() => onOpenBusiness(parentContactId))}
            sx={{
              cursor: "pointer",
              borderRadius: "var(--radius-md)",
              padding: "var(--space-3)",
              "&:hover": {
                backgroundColor: "var(--color-bg-surface-hover)",
              },
            }}
          >
            <ListItemIcon wrapper="brand">
              <HugeiconsIcon
                icon={Building03Icon}
                size={18}
                color="var(--color-text-brand)"
              />
            </ListItemIcon>
            <Stack gap="var(--space-1)">
              <Typography variant="subtitle2">{businessName}</Typography>
              {businessLocation && (
                <Typography
                  variant="caption"
                  sx={{ color: "var(--color-text-tertiary)" }}
                >
                  {businessLocation}
                </Typography>
              )}
            </Stack>
          </Inline>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: "var(--color-text-tertiary)",
              textAlign: "center",
              paddingBlock: "var(--space-6)",
              border: "1px dashed var(--color-border-subtle)",
              borderRadius: "var(--radius-md)",
            }}
          >
            {t("StandaloneContactNoBusiness")}
          </Typography>
        )}
      </Stack>
    </Card>
  );
};

export default ContactBusinessCard;
